import { SEARCH_INDEX, SearchItem } from "@/data/searchIndex";

export interface SearchResult extends SearchItem {
  score: number;
  matchedKeywords: string[];
}

/**
 * Tokenize a query string into lowercase words, stripping punctuation.
 */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1);
}

/**
 * Score a single search item against a query.
 * Scoring tiers (cumulative):
 *   +100  exact full-query match in title (case-insensitive)
 *   +80   exact full-query match in any keyword
 *   +40   each query token that exactly matches a keyword
 *   +20   each query token that exactly matches in title/description
 *   +10   each query token that is a substring of a keyword (partial)
 *   +5    each query token that is a substring of title/description (partial)
 *
 * Condition entries get a +15 boost so symptom searches surface first.
 */
export function scoreItem(item: SearchItem, query: string): SearchResult {
  const q = query.trim().toLowerCase();
  const tokens = tokenize(q);
  const titleLow = item.title.toLowerCase();
  const descLow = item.description.toLowerCase();
  const keywordsLow = item.keywords.map((k) => k.toLowerCase());

  let score = 0;
  const matchedKeywords: string[] = [];

  if (!q) return { ...item, score: 0, matchedKeywords: [] };

  // Tier 1 – exact full query in title
  if (titleLow.includes(q)) score += 100;

  // Tier 2 – exact full query in any keyword
  for (const kw of keywordsLow) {
    if (kw === q || kw.includes(q)) {
      score += 80;
      matchedKeywords.push(kw);
      break;
    }
  }

  // Per-token scoring
  for (const token of tokens) {
    let tokenMatched = false;

    // Exact keyword match
    for (const kw of keywordsLow) {
      if (kw === token) {
        score += 40;
        if (!matchedKeywords.includes(kw)) matchedKeywords.push(kw);
        tokenMatched = true;
        break;
      }
    }

    // Exact match in title or description
    if (titleLow.includes(token) || descLow.includes(token)) {
      score += 20;
      tokenMatched = true;
    }

    if (!tokenMatched) {
      // Partial keyword match
      for (const kw of keywordsLow) {
        if (kw.includes(token) || token.includes(kw)) {
          score += 10;
          if (!matchedKeywords.includes(kw)) matchedKeywords.push(kw);
          break;
        }
      }

      // Partial title/description match
      if (
        titleLow.split(" ").some((w) => w.startsWith(token)) ||
        descLow.split(" ").some((w) => w.startsWith(token))
      ) {
        score += 5;
      }
    }
  }

  // Boost conditions so symptom searches surface first
  if (item.category === "Condition" && score > 0) score += 15;

  return { ...item, score, matchedKeywords };
}

/**
 * Search the full index and return up to `limit` results sorted by score.
 */
export function search(query: string, limit = 6): SearchResult[] {
  if (!query.trim()) return [];

  return SEARCH_INDEX.map((item) => scoreItem(item, query))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Return the single best-matching route for a query (used for Enter-key navigation).
 */
export function getBestRoute(query: string): string | null {
  const results = search(query, 1);
  return results.length > 0 ? results[0].route : null;
}
